'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Copy, Check, LogIn } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

const demoAccounts = [
  {
    role: 'Super Admin',
    email: 'admin@softphonesaas.io',
    password: 'Demo@Admin2026',
    color: 'text-[hsl(var(--primary))]',
    bgColor: 'bg-[hsl(var(--primary)/0.1)]',
  },
  {
    role: 'Company Admin',
    email: 'jenna.torres@acmecorp.io',
    password: 'Demo@Admin2026',
    color: 'text-[hsl(var(--accent))]',
    bgColor: 'bg-[hsl(var(--accent)/0.1)]',
  },
  {
    role: 'Standard User',
    email: 'marcus.rivera@acmecorp.io',
    password: 'Demo@User2026',
    color: 'text-success',
    bgColor: 'bg-success/10',
  },
];

export default function LoginForm() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const handleCopy = (value: string, key: string) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedField(key);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const handleUseAccount = (email: string, password: string) => {
    setValue('email', email, { shouldValidate: true });
    setValue('password', password, { shouldValidate: true });
    toast.success('Demo credentials filled in');
  };

  // Backend integration point: POST /auth/login → Supabase auth.signInWithPassword
  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1400));

    const validAccount = demoAccounts.find(
      (a) => a.email === data.email && a.password === data.password
    );

    if (validAccount) {
      toast.success(`Welcome back — signed in as ${validAccount.role}`);
      setTimeout(() => router.push('/dashboard'), 600);
    } else {
      toast.error('Invalid credentials — use the demo accounts below to sign in');
    }
    setIsLoading(false);
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12 overflow-y-auto">
      <div className="w-full max-w-md">
        {/* Mobile logo */}
        <div className="flex items-center gap-2 mb-8 lg:hidden">
          <AppLogo size={32} />
          <span className="font-bold text-lg">
            SoftPhone<span className="text-gradient-primary">SaaS</span>
          </span>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[hsl(var(--text-primary))]">Sign in to your account</h2>
          <p className="mt-1.5 text-sm text-[hsl(var(--text-secondary))]">
            Enter your credentials to access the softphone platform
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-[hsl(var(--text-primary))] mb-1.5">
              Work Email
            </label>
            <input
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              suppressHydrationWarning
              className={`w-full bg-surface-elevated border rounded-input px-3.5 py-2.5 text-sm text-[hsl(var(--text-primary))] placeholder-[hsl(var(--text-muted))] outline-none transition-all duration-150
                focus:border-[hsl(var(--primary)/0.6)] focus:ring-2 focus:ring-[hsl(var(--primary)/0.15)]
                ${errors.email ? 'border-danger' : 'border-[hsl(var(--border))]'}`}
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Enter a valid email address',
                },
              })}
            />
            {errors.email && (
              <p className="mt-1.5 text-xs text-danger">{errors.email.message}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-[hsl(var(--text-primary))]">
                Password
              </label>
              <button
                type="button"
                className="text-xs text-[hsl(var(--primary))] hover:text-[hsl(var(--primary)/0.8)] transition-colors"
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••••"
                suppressHydrationWarning
                className={`w-full bg-surface-elevated border rounded-input px-3.5 py-2.5 pr-11 text-sm text-[hsl(var(--text-primary))] placeholder-[hsl(var(--text-muted))] outline-none transition-all duration-150
                  focus:border-[hsl(var(--primary)/0.6)] focus:ring-2 focus:ring-[hsl(var(--primary)/0.15)]
                  ${errors.password ? 'border-danger' : 'border-[hsl(var(--border))]'}`}
                {...register('password', {
                  required: 'Password is required',
                  minLength: { value: 6, message: 'Password must be at least 6 characters' },
                })}
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-secondary))] transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1.5 text-xs text-danger">{errors.password.message}</p>
            )}
          </div>

          {/* Remember me */}
          <div className="flex items-center gap-2">
            <input
              id="remember"
              type="checkbox"
              className="w-4 h-4 rounded border-[hsl(var(--border))] bg-surface-elevated accent-[hsl(var(--primary))] cursor-pointer"
              {...register('remember')}
            />
            <label htmlFor="remember" className="text-sm text-[hsl(var(--text-secondary))] cursor-pointer">
              Keep me signed in for 30 days
            </label>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-dark))] text-white font-semibold text-sm py-2.5 rounded-btn transition-all duration-150 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ minHeight: '42px' }}
          >
            {isLoading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <>
                <LogIn size={16} />
                Sign In
              </>
            )}
          </button>
        </form>

        {/* Demo Accounts */}
        <div className="mt-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1 h-px bg-[hsl(var(--border))]" />
            <p className="text-xs text-[hsl(var(--text-muted))] font-medium uppercase tracking-wider">
              Demo Accounts
            </p>
            <div className="flex-1 h-px bg-[hsl(var(--border))]" />
          </div>

          <div className="rounded-card border border-[hsl(var(--border))] overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[hsl(var(--border))] bg-surface">
                  <th className="text-left px-3 py-2 text-[hsl(var(--text-muted))] font-medium">Role</th>
                  <th className="text-left px-3 py-2 text-[hsl(var(--text-muted))] font-medium">Email</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {demoAccounts.map((account) => (
                  <tr
                    key={`demo-${account.role}`}
                    className="border-b border-[hsl(var(--border-subtle))] last:border-0 bg-surface-elevated hover:bg-[hsl(var(--surface-elevated)/0.7)] transition-colors"
                  >
                    <td className="px-3 py-2.5">
                      <span className={`font-semibold ${account.color}`}>{account.role}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[hsl(var(--text-secondary))] font-mono text-[11px] truncate max-w-[140px]">
                          {account.email}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleCopy(account.email, `email-${account.role}`)}
                          className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))] transition-colors flex-shrink-0"
                          title="Copy email"
                        >
                          {copiedField === `email-${account.role}` ? (
                            <Check size={11} className="text-success" />
                          ) : (
                            <Copy size={11} />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => handleUseAccount(account.email, account.password)}
                        className={`text-[10px] font-semibold px-2.5 py-1 rounded-badge ${account.bgColor} ${account.color} hover:opacity-80 transition-opacity`}
                      >
                        Use
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-[hsl(var(--text-muted))]">
          By signing in, you agree to our{' '}
          <span className="text-[hsl(var(--primary))] cursor-pointer hover:underline">Terms of Service</span>{' '}
          and{' '}
          <span className="text-[hsl(var(--primary))] cursor-pointer hover:underline">Privacy Policy</span>
        </p>
      </div>
    </div>
  );
}